import {
  CONSULTATION_PRIVACY_DISCLAIMER,
  INC_VARIANT_RECORDING_CONSENT,
  INC_VARIANT_RECORDING_DISCLAIMER,
} from '@/constants/privacyDisclaimers';
import { useConsultEligibleCalendar } from '@/context/calendar-context';
import { ImmediateNurseConsultationVariant } from '@/hooks/useImmediateNurseConsultVariant';
import ConfirmSmsNumberDialog from '@/pages/components/sms-verification/ConfirmSmsNumberDialog';
import { CtaButton, CtaButtonText, CtaSection } from '@/pages/components/styled/Cta';
import { Alert, Box, Button, Stack, TextField, Typography } from '@montugroup/design-system';
import AccessTime from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link, styled } from '@mui/material';
import type { ComponentProps } from 'react';
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form';
import 'react-phone-number-input/style.css';

export type FormValues = {
  firstName: string;
  lastName: string;
  phone: string;
  callType?: 'VOICE' | 'VIDEO';
};

function CustomPhoneInput({ ref, ...props }: ComponentProps<typeof TextField>) {
  return <TextField {...props} ref={ref} variant="standard" fullWidth />;
}

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(20),
  [theme.breakpoints.up('md')]: {
    marginBottom: 0,
  },
}));

const StartConsultationButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    marginTop: 20,
  },
}));

export type PersonalDetailFormWithoutCallTypeProps = {
  onSubmit: ReturnType<UseFormHandleSubmit<FormValues>>;
  immediateConsultVariant: ImmediateNurseConsultationVariant;
  submissionError: boolean;
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  showScheduleForLaterButton: boolean;
  smsConfirmationDialogOpen: boolean;
  onPhoneNumberConfirmationDialogClose: (trackEvent: boolean) => void;
  onStartConsultation: (data: FormValues) => Promise<void>;
};

export const PersonalDetailFormWithoutCallType = ({
  onSubmit,
  immediateConsultVariant,
  submissionError,
  control,
  errors,
  getValues,
  setValue,
  showScheduleForLaterButton,
  smsConfirmationDialogOpen,
  onPhoneNumberConfirmationDialogClose,
  onStartConsultation,
}: PersonalDetailFormWithoutCallTypeProps) => {
  const { handleShowCalendar } = useConsultEligibleCalendar();

  return (
    <Stack sx={{ minHeight: '70vh', flexDirection: 'column' }}>
      <form onSubmit={onSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', pb: 6, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 6 }}>
            {immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC
              ? `We just need a few details so we can connect you to one of our nurses.`
              : `Provide the below details to connect to a nurse.`}
          </Typography>

          {submissionError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Something went wrong. Please try again or schedule for later.
            </Alert>
          )}
          <Box sx={{ mb: 5 }}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label="First name"
                  fullWidth
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message}
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 5 }}>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: 'Last name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label="Last name"
                  fullWidth
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 5 }}>
            <PhoneInputWithCountry
              defaultCountry="AU"
              international
              value={getValues('phone')}
              inputComponent={CustomPhoneInput}
              onChange={(value: string) => setValue('phone', value)}
              error={Boolean(errors.phone)}
              helperText={errors.phone?.message}
              name="phone"
              control={control}
              rules={{
                required: 'Phone number is required',
                validate: (value: string) => (isValidPhoneNumber(value) ? undefined : 'Invalid phone number'),
              }}
              label={
                immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC
                  ? 'Phone number'
                  : 'Phone number (In case your call disconnects)'
              }
              data-testid="phone-number-input"
            />
          </Box>
          {immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC && (
            <Typography sx={{ mt: 4, mb: 6 }}>
              You will receive an SMS message for verification purposes. Simply follow the instructions to verify your
              phone number.
            </Typography>
          )}

          <StyledBox>
            <Alert severity="info" icon={<InfoOutlined />}>
              {immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC
                ? INC_VARIANT_RECORDING_DISCLAIMER
                : INC_VARIANT_RECORDING_CONSENT}
            </Alert>
          </StyledBox>

          <Box>
            {immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC ? (
              <CtaSection breakpoint="md">
                <CtaButton type="submit" variant="contained">
                  <CtaButtonText sx={{ fontWeight: 'bold' }}>Next</CtaButtonText>
                </CtaButton>
              </CtaSection>
            ) : (
              <StartConsultationButton type="submit" variant="contained" fullWidth startIcon={<PhoneIcon />}>
                <Typography sx={{ p: 2 }}>Start consultation</Typography>
              </StartConsultationButton>
            )}
            {showScheduleForLaterButton && (
              <Box>
                <Button
                  variant="outlined"
                  type="button"
                  startIcon={<CalendarTodayIcon />}
                  onClick={handleShowCalendar}
                  fullWidth
                >
                  Not ready now? Schedule for later
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </form>
      <ConfirmSmsNumberDialog
        open={smsConfirmationDialogOpen}
        onClose={onPhoneNumberConfirmationDialogClose}
        onSubmit={onStartConsultation}
      />
    </Stack>
  );
};

export type PersonalDetailsFormProps = {
  onSubmit: ReturnType<UseFormHandleSubmit<FormValues>>;
  submissionError: boolean;
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  trigger: UseFormTrigger<FormValues>;
  showScheduleForLaterButton: boolean;
  smsConfirmationDialogOpen: boolean;
  onPhoneNumberConfirmationDialogClose: (trackEvent: boolean) => void;
  onStartConsultation: (data: FormValues) => Promise<void>;
};

export const PersonalDetailsForm = ({
  onSubmit,
  submissionError,
  control,
  errors,
  getValues,
  setValue,
  showScheduleForLaterButton,
  smsConfirmationDialogOpen,
  onPhoneNumberConfirmationDialogClose,
  onStartConsultation,
}: PersonalDetailsFormProps) => {
  const { handleShowCalendar } = useConsultEligibleCalendar();
  return (
    <Stack sx={{ minHeight: '70vh', flexDirection: 'column' }}>
      <form onSubmit={onSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', pb: 6, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 6 }} color={'primary'}>
            Please provide your name and number below. Once we verify your number, we’ll sms you a consult link to join.
          </Typography>

          {submissionError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Something went wrong. Please try again or schedule for later.
            </Alert>
          )}

          <Box sx={{ mb: 5 }}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label="First name"
                  fullWidth
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message}
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 5 }}>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: 'Last name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label="Last name"
                  fullWidth
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 10 }}>
            <PhoneInputWithCountry
              defaultCountry="AU"
              international
              value={getValues('phone')}
              inputComponent={CustomPhoneInput}
              onChange={(value: string) => setValue('phone', value)}
              error={Boolean(errors.phone)}
              helperText={errors.phone?.message}
              name="phone"
              control={control}
              rules={{
                required: 'Phone number is required',
                validate: (value: string) => (isValidPhoneNumber(value) ? undefined : 'Invalid phone number'),
              }}
              label="Phone number"
              data-testid="phone-number-input"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Alert severity="info" icon={<AccessTime />}>
              <Typography component="span" fontWeight="bold">
                Average wait time
              </Typography>{' '}
              <Typography component="span">approx.</Typography>{' '}
              <Typography component="span" fontWeight="bold">
                5–10 minutes
              </Typography>
            </Alert>
          </Box>

          <Box sx={{ mb: { xs: 20, sm: 0 } }}>
            <Alert severity="info" icon={<InfoOutlined />}>
              <Typography component="span" fontWeight="bold">
                Calls are recorded and transcribed.{' '}
              </Typography>
              <Typography component="span">
                {CONSULTATION_PRIVACY_DISCLAIMER.text}
                <Link href={CONSULTATION_PRIVACY_DISCLAIMER.url} target="_blank" rel="noopener noreferrer">
                  {CONSULTATION_PRIVACY_DISCLAIMER.url}
                </Link>
              </Typography>
            </Alert>
          </Box>

          <Box>
            <CtaSection breakpoint="md" justifyContent={'flex-start'}>
              <CtaButton type="submit" variant="contained">
                <CtaButtonText sx={{ fontWeight: 'bold' }}>Continue</CtaButtonText>
              </CtaButton>
            </CtaSection>
            {showScheduleForLaterButton && (
              <Box>
                <Button
                  variant="outlined"
                  type="button"
                  startIcon={<CalendarTodayIcon />}
                  onClick={handleShowCalendar}
                  fullWidth
                >
                  Not ready now? Schedule for later
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </form>
      <ConfirmSmsNumberDialog
        open={smsConfirmationDialogOpen}
        onClose={onPhoneNumberConfirmationDialogClose}
        onSubmit={onStartConsultation}
      />
    </Stack>
  );
};
